import { SEAT_ORDER } from './types.js';
import type {
  BankruptcyTransfer,
  MatchCalculation,
  ResultInput,
  ResultOutput,
  Rule,
  Seat,
} from './types.js';
import { roundScorePoint } from './rounding.js';

/** 合計が 0 かどうかを判定する許容誤差。ウマに小数を設定した場合の丸め誤差対策 */
const EPSILON = 1e-9;

/**
 * 席順。未入力は最後に回す（§5.1 Step 1 の NULLS LAST）。
 * 同点者には席の入力が保証されているため、通常この Infinity 同士が比較される
 * ことはない。
 */
function seatOrderOf(seat: Seat | null | undefined): number {
  return seat ? SEAT_ORDER[seat] : Number.POSITIVE_INFINITY;
}

/** オカ。(返し点 − 配給原点) × 人数 を千点単位にしたもの（§5.1 Step 4） */
export function calculateOka(rule: Rule): number {
  return ((rule.returnPoints - rule.startPoints) * rule.playerCount) / 1000;
}

/** 飛びの判定（§5.1 Step 5）。bankruptcy.enabled とは独立に判定する */
export function isBankrupt(rawScore: number, rule: Rule): boolean {
  return rule.bankruptcy.threshold === 'below_zero' ? rawScore < 0 : rawScore <= 0;
}

/**
 * 対局1件の精算ポイントを計算する（仕様書 §5.1）。
 *
 * 入力の妥当性は検証しない。保存前には validateMatch を必ず先に通すこと。
 * ここでは不正な入力に対しても例外を投げず、防御的な既定値で計算を続ける
 * （プレビュー中は入力途中の状態が渡されるため）。
 *
 * @param inputs 参加者ごとの素点。順序は問わない
 * @param rule   対局に適用するルール（保存時はこれがスナップショットになる）
 * @returns      順位の昇順に並べた計算結果
 */
export function calculateMatch(inputs: ResultInput[], rule: Rule): MatchCalculation {
  const oka = calculateOka(rule);

  if (inputs.length === 0) {
    return { results: [], transfers: [], zeroSumAdjustment: 0, oka };
  }

  // --- Step 1. 順位判定 ---------------------------------------------------
  // 素点の降順 → 席順の昇順（未入力は後ろ）→ 入力順。
  // 3つ目のキーがあることでソートが全順序になり、順位は必ず 1〜N に一意に定まる。
  // 同着は発生させない（§5.1 Step 1）。
  const ordered = inputs
    .map((input, inputIndex) => ({ input, inputIndex }))
    .sort((a, b) => {
      if (a.input.rawScore !== b.input.rawScore) {
        return b.input.rawScore - a.input.rawScore;
      }
      // 減算ではなく大小比較で返す。席未入力は Infinity なので、両方未入力なら
      // Infinity - Infinity = NaN となってソートが壊れ、片方だけ未入力なら
      // 差が Infinity になって有限判定に弾かれる。等値かどうかを先に見れば
      // どちらの罠も踏まない。
      const sa = seatOrderOf(a.input.seat);
      const sb = seatOrderOf(b.input.seat);
      if (sa !== sb) {
        return sa < sb ? -1 : 1;
      }
      return a.inputIndex - b.inputIndex;
    });

  const results: ResultOutput[] = ordered.map(({ input }, i) => {
    const rank = i + 1;

    // --- Step 2. 素点ポイント --------------------------------------------
    const scorePoint = roundScorePoint(input.rawScore - rule.returnPoints, rule.rounding);

    // --- Step 3. ウマ -----------------------------------------------------
    // 順位が一意に定まるため分割・折半は不要（§5.1 Step 3）。
    const umaPoint = rule.uma[rank - 1] ?? 0;

    // --- Step 4. オカ -----------------------------------------------------
    const okaPoint = rank === 1 ? oka : 0;

    // 直前の行と素点が同じなら、席順によって下位に置かれた行
    // （対局詳細で「同点（席順で下位）」を表示する）
    const prev = ordered[i - 1];
    const tiedButLower = prev !== undefined && prev.input.rawScore === input.rawScore;

    return {
      ...input,
      rank,
      isBankrupt: isBankrupt(input.rawScore, rule),
      scorePoint,
      umaPoint,
      okaPoint,
      bankruptcyPoint: 0,
      totalPoint: 0,
      tiedButLower,
    };
  });

  // --- Step 5. 飛び賞 -----------------------------------------------------
  const transfers: BankruptcyTransfer[] = [];
  const top = results[0]!;

  if (rule.bankruptcy.enabled) {
    const byId = new Map(results.map((r) => [r.memberId, r]));

    for (const p of results) {
      if (!p.isBankrupt) continue;

      let receiver: ResultOutput | null;
      if (rule.bankruptcy.payTo === 'buster') {
        // 飛ばした人が未指定、または参加者として見つからない場合は fallback に従う。
        // 後者はバリデーションで INVALID_BUSTER として弾かれるが、防御的に扱う。
        const buster = p.bustedBy ? (byId.get(p.bustedBy) ?? null) : null;
        receiver = buster ?? (rule.bankruptcy.fallbackPayTo === 'top' ? top : null);
      } else {
        receiver = top;
      }

      // fallbackPayTo=none で受取人がいない、あるいは自分自身が受取人になる場合は
      // 授受を発生させない（§5.1 Step 5 補足）。トップ自身が飛ぶ理論上のケースが
      // これに当たる。
      if (receiver === null || receiver.memberId === p.memberId) {
        transfers.push({ fromMemberId: p.memberId, toMemberId: null, point: 0 });
        continue;
      }

      p.bankruptcyPoint -= rule.bankruptcy.point;
      receiver.bankruptcyPoint += rule.bankruptcy.point;
      transfers.push({
        fromMemberId: p.memberId,
        toMemberId: receiver.memberId,
        point: rule.bankruptcy.point,
      });
    }
  }

  // --- Step 6. 合計とゼロサム検証 ------------------------------------------
  for (const r of results) {
    r.totalPoint = r.scorePoint + r.umaPoint + r.okaPoint + r.bankruptcyPoint;
  }

  const sum = results.reduce((acc, r) => acc + r.totalPoint, 0);
  let zeroSumAdjustment = 0;

  if (Math.abs(sum) > EPSILON && rule.zeroSumAdjust === 'top') {
    // 丸めで生じたズレを1位に寄せて合計を 0 に戻す。
    // 補正した旨は対局詳細に注記する（§5.1 Step 6）。
    zeroSumAdjustment = -sum;
    top.totalPoint += zeroSumAdjustment;
  }

  return { results, transfers, zeroSumAdjustment, oka };
}
